import { useMemo } from "react";

import { cloneDeep } from "lodash";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";

import { Tooltip } from "@components/tooltip-container";
import { useWizard, Wizard } from "@components/wizard";
import { Weekday } from "@features/mmm/utils/date-stretch-compress-helpers";
import { getIsoDate } from "@features/mmm/utils/formatters";
import {
  getClosestUpcomingTuesday,
  getDayOfWeekForIsoDate,
} from "@features/mmm/utils/get_cycle_weeks";
import { type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { VERIFY_CHANNELS } from "@north-beam/nb-common";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { Modal } from "@shared/modal";

import {
  useCreateMediaPlan,
  useMediaPlanTriggerModelTraining,
} from "../hooks/use-create-media-plan";
import { useMediaPlanData } from "../hooks/use-media-plan";
import { useUpdateMediaPlan } from "../hooks/use-update-media-plan";
import { useUpdateMediaPlanDraftStep } from "../hooks/use-update-media-plan-draft-step";

import { DEPRECATED_WIZARD_STEPS } from "./consts";
import { type MediaPlan as BasicMediaPlan } from "./create-basic-media-plan-modal";
import { MediaPlanModalTabs } from "./media-plan-modal-tabs";
import {
  AdditionalDataStep,
  BasicInformationStep,
  ChannelsStep,
  PerformanceGoalStep,
  PromotionCalendarStep,
} from "./steps";
import { useSelectedMediaPlanModal } from "./use-selected-media-plan-modal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedMediaPlan: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList;
}

export type MediaPlan = BasicMediaPlan;

export const CreateMediaPlanModal = ({
  isOpen,
  onClose,
  selectedMediaPlan,
}: Props) => {
  const { modelConfigurations } = useMediaPlanData();

  const { addToast } = useToasts();
  const form = useForm<MediaPlan>({
    defaultValues: {
      id: selectedMediaPlan.id,
      name: selectedMediaPlan.name ?? "",
      start_date: selectedMediaPlan.date_range.start_date,
      end_date: selectedMediaPlan.date_range.end_date,
      model_configuration_id: selectedMediaPlan.model_config?.config_id ?? "",
    },
  });

  const name = form.watch("name");
  const model_configuration_id = form.watch("model_configuration_id");
  const start_date = form.watch("start_date");
  const end_date = form.watch("end_date");

  const isBasicInfoFormValid = useMemo(
    () => name && model_configuration_id && start_date && end_date,
    [name, model_configuration_id, start_date, end_date],
  );

  const WIZARD_STEPS = DEPRECATED_WIZARD_STEPS;

  const { currentIndex, goToNext, goToPrevious, setStep } = useWizard();

  const _onClose = () => {
    form.reset();
    setStep(0);
    onClose();
  };

  const { isUpdateMediaPlanDraftStepLoading } = useUpdateMediaPlanDraftStep({
    selectedMediaPlan,
    draftStep: VERIFY_CHANNELS,
  });
  const { triggerModelTraining, isTriggerModelTrainingLoading } =
    useMediaPlanTriggerModelTraining();

  const { isDraft } = useSelectedMediaPlanModal();
  const onSubmit = async () => {
    if (isDraft) {
      await triggerModelTraining({
        variables: {
          cycle_id: selectedMediaPlan.id,
          model_config_id: model_configuration_id,
        },
      });
      addToast(
        <div>
          Thanks! In about ~1 hour, you will be ready to see Model Insights on
          the “View Incremental ROAS/CAC” tab and start creating budgets on the
          “Explore Mixes” tab.
        </div>,
        { appearance: "success" },
      );
    }
    _onClose();
  };

  const hasVerifiedAllSteps = useMemo(
    () =>
      Object.values(selectedMediaPlan.step_tracker)
        .filter((step) => step !== "mediaPlanStepTracker")
        .every((step) => step.verified),
    [selectedMediaPlan],
  );

  const wizardSteps = useMemo(() => {
    return [
      {
        children: (
          <BasicInformationStep
            form={form}
            currentIndex={currentIndex}
            modelConfigurations={modelConfigurations}
            model_configuration_id={model_configuration_id}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
      {
        children: (
          <ChannelsStep
            currentIndex={currentIndex}
            spendModelDataColumns={selectedMediaPlan.spend_model_data_columns}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
      {
        children: (
          <PerformanceGoalStep
            currentIndex={currentIndex}
            kpis={selectedMediaPlan.kpis ?? []}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
      {
        children: (
          <PromotionCalendarStep
            currentIndex={currentIndex}
            calendarOfEvents={selectedMediaPlan.calendar_of_events}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
      {
        children: (
          <AdditionalDataStep
            currentIndex={currentIndex}
            selectedMediaPlan={selectedMediaPlan}
          />
        ),
      },
    ];
  }, [
    currentIndex,
    selectedMediaPlan,
    modelConfigurations,
    form,
    model_configuration_id,
  ]);

  const { createMediaPlan, isCreateMediaPlanLoading } = useCreateMediaPlan();
  const { updateMediaPlan, isUpdateMediaPlanLoading } = useUpdateMediaPlan();
  const goToNextInWizard = async () => {
    if (currentIndex === 0) {
      const selectedModelConfig = modelConfigurations.find(
        (config) => config.config_id === model_configuration_id,
      );
      const mediaPlanData = cloneDeep(form.getValues());

      if (
        getDayOfWeekForIsoDate(
          mediaPlanData.end_date || getIsoDate(new Date()),
        ) !== Weekday.TUESDAY
      ) {
        mediaPlanData.end_date = getClosestUpcomingTuesday(
          mediaPlanData.end_date || getIsoDate(new Date()),
        );
      }
      if (
        !selectedMediaPlan.id &&
        selectedModelConfig &&
        model_configuration_id &&
        name
      ) {
        // NOTE: Not fixing typing because this component is deprecated
        await createMediaPlan({
          variables: {
            ...mediaPlanData,
            client_seed_dataset_id: selectedModelConfig.client_seed_dataset,
          },
        });
      } else if (
        selectedMediaPlan.id &&
        selectedModelConfig &&
        (selectedMediaPlan.name !== name ||
          selectedMediaPlan.date_range.start_date !== start_date ||
          selectedMediaPlan.date_range.end_date !== end_date ||
          selectedMediaPlan.model_config?.config_id !== model_configuration_id)
      ) {
        await updateMediaPlan({
          // TODO: Fix typing
          variables: {
            ...mediaPlanData,
            client_seed_dataset_id: selectedModelConfig.client_seed_dataset,
          },
        });
      }

      goToNext();
    } else {
      goToNext();
    }
  };

  const isLoading =
    isTriggerModelTrainingLoading ||
    isUpdateMediaPlanDraftStepLoading ||
    isCreateMediaPlanLoading ||
    isUpdateMediaPlanLoading;

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={_onClose}
      hideCloseButton
      bodyClassName="px-0 py-0"
    >
      <div className="p-1.5 bg-gray-3 flex gap-1.5 justify-between h-[600px] relative">
        <div className="absolute top-10 right-8" onClick={_onClose}>
          <i className="fa-regular fa-close text-cosmic cursor-pointer" />
        </div>
        <MediaPlanModalTabs
          className="w-1/3 max-w-[286px] bg-v2-primary rounded border border-v2-secondary"
          currentIndex={currentIndex}
          setStep={setStep}
        />
        <div className="flex-grow bg-v2-primary rounded border border-v2-secondary flex flex-col justify-between">
          <Wizard
            className="p-6 pb-0 overflow-y-scroll"
            currentIndex={currentIndex}
            goToNext={goToNext}
            goToPrevious={goToPrevious}
            setStep={setStep}
            submitText="Create Media Plan"
            onSubmit={form.handleSubmit(onSubmit)}
            shouldHideDefaultFooter
            steps={wizardSteps}
          />
          <div className="px-6 bg-[#FAFAFA] h-16 flex items-center justify-end space-x-2">
            <TertiaryButton onClick={_onClose}>Cancel</TertiaryButton>
            {currentIndex > 0 && (
              <SecondaryButton onClick={goToPrevious}>Back</SecondaryButton>
            )}
            {currentIndex < WIZARD_STEPS.length - 1 && (
              <PrimaryButton
                onClick={goToNextInWizard}
                loading={isLoading}
                disabled={
                  (currentIndex === 0 && !isBasicInfoFormValid) || isLoading
                }
              >
                Next
              </PrimaryButton>
            )}
            {currentIndex === WIZARD_STEPS.length - 1 && isDraft && (
              <Tooltip
                content="Please verify all steps before creating a new plan"
                placement="top"
                noInfoCircle
              >
                <PrimaryButton
                  loading={isLoading}
                  isDisabled={
                    !hasVerifiedAllSteps || isTriggerModelTrainingLoading
                  }
                  onClick={form.handleSubmit(onSubmit)}
                >
                  CREATE NEW PLAN
                </PrimaryButton>
              </Tooltip>
            )}
            {currentIndex === WIZARD_STEPS.length - 1 && !isDraft && (
              <PrimaryButton onClick={_onClose}>CLOSE</PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
